import { Injectable } from '@angular/core';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
    GraphDataItem,
    ValueBridgeGraphData
} from '../../../shared/state/value-bridge/sb-value-bridge.model';
import { Observable, of } from 'rxjs';
import { CLDRService } from '../../../shared/services/cldr/cldr.service';
import { SessionQuery } from '../../../core/state/session/session.query';
import { SbHost, SbPath } from '@models';
import { SbDataService } from '@services';
import { Currency } from '../../../shared/components/currency-selector/currency-country-codes';
import { SbValueBridgeStore } from './sb-value-bridge.store';

/** @deprecated Re-adding just allow the legacy value bridge component to work. DO NOT USE ELSEWHERE! */
@Injectable({ providedIn: 'root' })
export class SbValueBridgeService {
    constructor(
        private config: AppConfigQuery,
        private valueBridgeStore: SbValueBridgeStore,
        private http: HttpClient,
        private cldr: CLDRService,
        private sessionQuery: SessionQuery,
        private sbDataService: SbDataService
    ) {}

    getUrl(path: SbPath): string {
        return this.sbDataService.getResourceUrl(path, SbHost.ClickhouseBaseUrl);
    }

    /**
     * Fetches value bridge data based on the provided parameters and an optional group.
     * @param params The query parameters for the request.
     * @param resource Request the data by 'portfolio' or 'instrument-type' (asset-type)
     * @param group Optional group path parameter.
     * @returns An Observable of ValueBridgeGraphData.
     */
    getValueBridgeData(
        params: {
            base_currency: Currency;
            start_date: string;
            end_date: string;
            chartType: 'portfolio' | 'asset-type';
            account_ids: string[];
        },
        group?: string
    ): Observable<ValueBridgeGraphData> {
        if (params.account_ids && params.account_ids.length > 0) {
            let queryParams = new HttpParams()
                .set('base_currency', params.base_currency)
                .set('start_date', params.start_date)
                .set('end_date', params.end_date);

            const accountIdsParam = params.account_ids.join(',');
            queryParams = queryParams.set('account_ids', accountIdsParam);

            let resourcePath = `v1/value-bridge/${params.chartType}`;
            if (group && group.trim() !== '') {
                // just to include the '/group' keyword in the path
                resourcePath += `/group`;
                //both asset-type and asset_type are used in the url, we are fixing this conflict here
                queryParams = queryParams.set(
                    params.chartType === 'asset-type' ? 'asset_type' : 'portfolio',
                    group.trim()
                );
            }

            const url = this.getUrl({ resource: resourcePath });

            return this.http.get<ValueBridgeGraphData>(url, {
                params: queryParams,
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${this.sessionQuery.effectiveAccessToken}`
                }
            });
        } else {
            return of(null);
        }
    }

    public static calculateMarginBottomsOfBars(barValues: number[]): number[] {
        let marginBottoms: number[] = new Array(barValues.length).fill(0);

        for (let i = 0; i < barValues.length; i++) {
            if (i === 0 || i === barValues.length - 1) {
                marginBottoms[i] = 0;
            } else {
                const prevNonZeroIndex = SbValueBridgeService.findPreviousNonZeroIndex(
                    barValues,
                    i
                );
                const previousNonZeroValue = barValues[prevNonZeroIndex];

                //Currently, we are excluding the values with zero at the component level.
                //However, the logic to handle zero values is retained for potential future scenarios. MK.
                if (i > 0 && prevNonZeroIndex >= 0) {
                    if (barValues[prevNonZeroIndex] > 0 && barValues[i] > 0) {
                        // + +
                        marginBottoms[i] =
                            marginBottoms[prevNonZeroIndex] +
                            Math.abs(previousNonZeroValue);
                    } else if (barValues[prevNonZeroIndex] > 0 && barValues[i] < 0) {
                        // + -
                        marginBottoms[i] =
                            marginBottoms[prevNonZeroIndex] +
                            barValues[prevNonZeroIndex] +
                            barValues[i];
                    } else if (barValues[prevNonZeroIndex] < 0 && barValues[i] < 0) {
                        // - -
                        marginBottoms[i] = marginBottoms[prevNonZeroIndex] + barValues[i];
                    } else if (barValues[prevNonZeroIndex] < 0 && barValues[i] > 0) {
                        // - +
                        marginBottoms[i] = marginBottoms[prevNonZeroIndex];
                    } else if (barValues[prevNonZeroIndex] < 0 && barValues[i] === 0) {
                        // - 0
                        marginBottoms[i] = marginBottoms[prevNonZeroIndex];
                    } else if (barValues[prevNonZeroIndex] > 0 && barValues[i] === 0) {
                        // + 0
                        marginBottoms[i] =
                            marginBottoms[prevNonZeroIndex] + barValues[prevNonZeroIndex];
                    }
                } else {
                    marginBottoms[i] = 0;
                }
            }
        }

        return marginBottoms;
    }

    public static getLineProperties(
        leftBar: GraphDataItem,
        rightBar: GraphDataItem,
        leftBarIndex: number,
        maxValue: number,
        maxBarHeight: number,
        barWidth: number,
        graphData: ValueBridgeGraphData
    ): any {
        if (!rightBar) {
            return {
                display: 'none'
            };
        }

        const unit = 'px';

        let prevNonZeroIndex = leftBarIndex;
        for (let i = leftBarIndex; i >= 0; i--) {
            if (graphData.graph[i] && graphData.graph[i].value !== 0) {
                prevNonZeroIndex = i;
            }
        }
        let bottomValue = this.calculateBottomOfALine(
            leftBar,
            maxValue,
            maxBarHeight,
            unit
        );

        return {
            display: 'block',
            position: 'absolute',
            borderStyle: 'none',
            borderBottom: 'var(--label-border)',
            left: `${barWidth}${unit}`,
            bottom: bottomValue,
            width: `${barWidth}${unit}`
        };
    }

    //region Helper functions

    public static calculateScalingFactor(maxValue: number, maxBarHeight: number): number {
        return maxBarHeight / maxValue;
    }

    public static calculateHeightOfBar(
        value: number,
        maxValue: number,
        maxBarHeight: number
    ): number {
        const scalingFactor = SbValueBridgeService.calculateScalingFactor(
            maxValue,
            maxBarHeight
        );
        return Math.abs(value) * scalingFactor;
    }

    public static calculateLeftPositionOfBar(index: number, barWidth: number): number {
        return index * barWidth;
    }

    public static calculateMarginBottomOfBar(
        index: number,
        maxValue: number,
        maxBarHeight: number,
        marginBottoms: number[]
    ): number {
        const scalingFactor = SbValueBridgeService.calculateScalingFactor(
            maxValue,
            maxBarHeight
        );
        return marginBottoms[index] * scalingFactor;
    }

    public static calculateMarginBottoms(graphData: ValueBridgeGraphData) {
        return SbValueBridgeService.calculateMarginBottomsOfBars(
            graphData.graph.map((v) => v.value)
        );
    }

    private static findPreviousNonZeroIndex(
        barValues: number[],
        currentIndex: number
    ): number {
        for (let i = currentIndex - 1; i >= 0; i--) {
            if (barValues[i] !== 0) {
                return i;
            }
        }
        return -1;
    }

    private static calculateBottomOfALine(
        leftBar: GraphDataItem,
        maxValue: number,
        maxBarHeight: number,
        unit: string
    ) {
        let ret;

        const leftBarHeight = SbValueBridgeService.calculateHeightOfBar(
            leftBar.value,
            maxValue,
            maxBarHeight
        );

        if (leftBar.value === 0) {
            ret = '0px';
        } else if (leftBar.value > 0) {
            ret = leftBarHeight - (leftBarHeight > 3 ? 1 : 0.5) + unit;
        } else if (leftBar.value < 0) {
            ret = (leftBarHeight > 3 ? -1 : -0.5) + unit;
        } else {
            ret = '0px';
        }

        return ret;
    }

    //endregion
}
